/**
 * ProgressBar Component
 * A flexible progress indicator with accessibility features and animation
 */

import React from 'react';
import styled, { css, keyframes } from 'styled-components';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface ProgressBarProps {
  /** Progress value (0-100) */
  progress: number;
  /** Custom text to display (defaults to percentage) */
  text?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'primary' | 'success' | 'warning' | 'danger';
  /** Whether to show percentage text */
  showPercentage?: boolean;
  /** Whether to animate the progress */
  animated?: boolean;
  /** Additional CSS class */
  className?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const shimmerAnimation = keyframes`
  0% { 
    background-position: -200% 0; 
  }
  100% { 
    background-position: 200% 0; 
  }
`;

const ProgressContainer = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  width: 100%;
  margin: var(--space-sm) 0;
`;

const ProgressTrack = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  width: 100%;
  background: var(--surface-strong);
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 1px solid var(--border);

  /* Size variants */
  ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return `
          height: 4px;

          /* Mobile: Increase visibility */
          @media (max-width: 767px) {
            height: 6px;
          }
        `;
      case 'lg':
        return `
          height: 16px;

          /* Mobile: Increase visibility */
          @media (max-width: 767px) {
            height: 20px;
          }
        `;
      default: // md
        return `
          height: 8px;

          /* Mobile: Increase visibility */
          @media (max-width: 767px) {
            height: 10px;
          }
        `;
    }
  }}
`;

const ProgressFill = styled.div<{
  $progress: number;
  $variant: 'primary' | 'success' | 'warning' | 'danger';
  $animated: boolean;
  $size: 'sm' | 'md' | 'lg';
}>`
  height: 100%;
  border-radius: var(--radius-sm);
  transition: width 0.3s ease;
  position: relative;
  width: ${({ $progress }) => Math.min(100, Math.max(0, $progress || 0))}%;

  background: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return 'var(--success-600)';
      case 'warning':
        return 'var(--warning-600)';
      case 'danger':
        return 'var(--danger-600)';
      default:
        return 'var(--brand-600)';
    }
  }};

  ${({ $animated }) =>
    $animated &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.3) 50%,
          transparent 100%
        );
        animation: ${shimmerAnimation} 1.5s infinite;
      }
    `}

  &:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
  }
`;

const ProgressText = styled.span<{ $size: 'sm' | 'md' | 'lg' }>`
  display: block;
  margin-top: var(--space-xs);
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return 'var(--font-size-sm)';
      case 'lg':
        return 'var(--font-size-base)';
      default: // md
        return 'var(--font-size-sm)';
    }
  }};
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-align: center;
`;

// ============================================================================
// Component
// ============================================================================

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  text,
  size = 'md',
  variant = 'primary',
  showPercentage = true,
  animated = false,
  className,
  'aria-label': ariaLabel,
  ...rest
}) => {
  // Ensure progress is within bounds and handle edge cases
  const safeProgress =
    typeof progress === 'number' && !isNaN(progress) ? progress : 0;
  const clampedProgress = Math.min(100, Math.max(0, safeProgress));

  // Generate display text
  const displayText =
    text || (showPercentage ? `${Math.round(clampedProgress)}%` : '');

  // Generate accessibility label
  const defaultAriaLabel = `Progress: ${Math.round(clampedProgress)}%`;
  const finalAriaLabel = ariaLabel || defaultAriaLabel;

  return (
    <ProgressContainer $size={size} className={className} {...rest}>
      <ProgressTrack
        $size={size}
        role="progressbar"
        aria-valuenow={Math.round(clampedProgress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={finalAriaLabel}
      >
        <ProgressFill
          $progress={clampedProgress}
          $variant={variant}
          $animated={animated}
          $size={size}
        />
      </ProgressTrack>
      {displayText && <ProgressText $size={size}>{displayText}</ProgressText>}
    </ProgressContainer>
  );
};

ProgressBar.displayName = 'ProgressBar';
